import { create } from 'zustand';
import type { RecipientActivity } from '../types/activity';

type ViewStatus = 'new' | 'read';

interface ActivityListState {
  activities: RecipientActivity[];
  unreadCount: number;
  readCount: number;
  loading: boolean;
  hasMore: boolean;
  cursorId: string | undefined;
  view: ViewStatus;
  setActivities: (activities: RecipientActivity[]) => void;
  appendActivities: (activities: RecipientActivity[]) => void;
  setUnreadCount: (count: number) => void;
  setReadCount: (count: number) => void;
  setLoading: (loading: boolean) => void;
  setHasMore: (hasMore: boolean) => void;
  setCursorId: (id: string | undefined) => void;
  setView: (view: ViewStatus) => void;
  /** Drop one activity from the in-memory list and move it from the unread
   *  tally to the read tally — the local mirror of a server-side mark-read,
   *  used when a card consumes itself without a list refetch (see
   *  ActivityListingStatusCard's auto-read). No-op if the id isn't present,
   *  so a double-fire can't double-count. */
  consumeActivity: (id: string) => void;
  reset: () => void;
}

export const useActivityListStore = create<ActivityListState>((set) => ({
  activities: [],
  unreadCount: 0,
  readCount: 0,
  loading: false,
  hasMore: false,
  cursorId: undefined,
  view: 'new',
  setActivities: (activities) => set({ activities }),
  appendActivities: (activities) => set((s) => ({ activities: [...s.activities, ...activities] })),
  setUnreadCount: (unreadCount) => set({ unreadCount }),
  setReadCount: (readCount) => set({ readCount }),
  setLoading: (loading) => set({ loading }),
  setHasMore: (hasMore) => set({ hasMore }),
  setCursorId: (cursorId) => set({ cursorId }),
  setView: (view) => set({ view }),
  consumeActivity: (id) =>
    set((s) => {
      const activities = s.activities.filter((a) => a.id !== id);
      if (activities.length === s.activities.length) return s;
      return {
        activities,
        unreadCount: Math.max(0, s.unreadCount - 1),
        readCount: s.readCount + 1,
      };
    }),
  reset: () => set({ activities: [], unreadCount: 0, readCount: 0, loading: false, hasMore: false, cursorId: undefined, view: 'new' }),
}));
